/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React from 'react'
import { useHistory } from 'react-router-dom'
import { type Question } from 'types/question'
import { type Response } from 'types/response'
import { type SessionObject } from 'types/session-object'
import Select, { type Option } from 'ui/Select'

import { QuestionInfo } from '@/components/QuestionInfo'
import TextField from '@/ui/molecules/TextField'
import { QuestionTitle } from '@/ui/Text'
import { getDisabledFlagsByMinAndMax } from '@/utils/getDisabledFlagsByMinAndMax'

import { hasText } from '../../../utils/helpers'
import ZentakeFormContext from '../ZentakeFormContext'

function DropdownMultipleQuestion({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}: {
  question: Question
  isOfficeUse: boolean
  response: Response
  sessions: SessionObject | undefined
  mode?: 'preview' | 'edit'
}) {
  const history = useHistory()
  const isBuilderForm = history?.location?.pathname?.includes('/forms/')

  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  const answer = formContext.getAnswer(question)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const choices: string[] = React.useMemo(
    () => (answer && answer.answer_raw ? answer.answer_raw.split(',') : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answer?.answer_raw]
  )

  const options = React.useMemo(
    () =>
      question.question_choices.map((choice) => ({
        id: choice.id,
        value: choice.text_value,
        label: choice.text_value,
      })),
    [question.question_choices]
  )

  function handleChange(options: Option[] | null) {
    const choiceIds = options?.map((option) => option.id)?.join(',') || ''
    formContext.onAnswerChanged(question, { choiceId: choiceIds })
  }

  const selectedOptions = options?.filter((option) =>
    choices.includes(String(option.id))
  )

  const { disableMin, disableMax, greaterThanMax } =
    getDisabledFlagsByMinAndMax({
      min: question?.min_value,
      max: question?.max_value,
      total: selectedOptions?.length,
    })

  const hasAnswer = Boolean(selectedOptions?.length)

  let shouldShowRangeTextOnlyBuilder = false
  if ((question?.min_value || question?.max_value) && isBuilderForm) {
    shouldShowRangeTextOnlyBuilder = true
  }

  let rangeText = ''
  if (question?.min_value && question?.max_value) {
    rangeText = `You must select between ${question?.min_value} and ${question?.max_value} options`
  } else if (question?.min_value) {
    rangeText = `The minimum required number of selections is ${question?.min_value}`
  } else if (question?.max_value) {
    rangeText = `You can select up to ${question?.max_value} options`
  }

  let shouldShowRangeText = false
  if (
    (disableMin || disableMax || shouldShowRangeTextOnlyBuilder) &&
    (hasAnswer || isBuilderForm) &&
    !greaterThanMax
  ) {
    shouldShowRangeText = true
  }

  return (
    <div>
      {readonly ? (
        <TextField
          disabled
          isOfficeUse={isOfficeUse}
          isRequired={question.is_required}
          label={question.text}
          value={selectedOptions.map((option) => option.value).join(', ')}
        />
      ) : (
        <>
          <QuestionTitle
            isOfficeUse={isOfficeUse || officeUsePreview}
            isRequired={question.is_required}
          >
            {question.text}
          </QuestionTitle>
          <Select
            isMulti
            showOptions
            data-form-id={response?.form?.id}
            data-packet-id={
              sessions?.fillResponseCustomer?.packetResponse?.packet?.id
            }
            data-response-id={response?.id}
            disabled={readonly}
            errorLabel={errorMessage}
            hasError={hasError}
            id={`q-${question.id}`}
            isOfficeUse={isOfficeUse}
            isRequired={question.is_required}
            // @ts-ignore
            onChange={(options: Option[] | null) => handleChange(options)}
            options={options}
            value={selectedOptions}
          />
          {shouldShowRangeText ? (
            <QuestionInfo className="mt-1">{rangeText}</QuestionInfo>
          ) : null}
        </>
      )}
    </div>
  )
}

export default React.memo(DropdownMultipleQuestion)
